import clsx from 'clsx'
import { ArrowDownUp, Info, Search } from 'lucide-react'
import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { reportInput } from '../ui'
import { dayHead, dorSections, fmt0, fmt1, fmtCell, pctTone, pick, UNTRACKED_ROWS } from './dorFormat'
import type { DorMonth, DorReportPayload, DorVehicle } from './dorTypes'

function useScrollToEnd(dep: unknown) {
  const ref = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const el = ref.current
    if (el) el.scrollLeft = el.scrollWidth
  }, [dep])
  return ref
}

const wrap = 'w-full max-w-full overflow-auto overscroll-contain rounded-xl border border-slate-200/80 bg-white shadow-sm max-h-[calc(100vh-17rem)] min-h-[20rem]'
const th = 'sticky top-0 z-[2] whitespace-nowrap border-b border-slate-200 bg-slate-50/95 px-3 py-2 text-[11px] font-semibold text-slate-500 backdrop-blur'
const td = 'whitespace-nowrap px-3 py-[7px] text-right text-[12.5px] tabular-nums'
const labelCell = 'sticky left-0 z-[1] whitespace-nowrap bg-white px-4 py-[7px] text-left text-[12.5px]'

function DayTh({ iso, active }: { iso: string; active: boolean }) {
  const h = dayHead(iso)
  return (
    <th className={clsx(th, 'min-w-[4.75rem] text-right', active && '!bg-amber-50 text-amber-900')}>
      <div className={clsx('text-[12px]', active ? 'text-amber-900' : 'text-slate-700')}>{h.d}</div>
      <div className="font-normal text-slate-400">{h.w}</div>
    </th>
  )
}

/* ------------------------------------------------------------------ Ops Data */
export function OpsSheet({ data, days }: { data: DorReportPayload; days: string[] }) {
  const sections = useMemo(() => dorSections(data.vendors), [data.vendors])
  const byDate = useMemo(() => new Map(data.days.map((d) => [d.date, d])), [data.days])
  const ref = useScrollToEnd(`${data.date}:${days.length}`)
  const months: [string, string, DorMonth][] = [
    ['prev', data.months.prev.label, data.months.prev],
    ['cur', `${data.months.cur.label} MTD`, data.months.cur],
  ]
  const cols = 1 + months.length + days.length

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div ref={ref} className={wrap}>
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr>
              <th className={clsx(th, 'left-0 z-[3] min-w-[16rem] px-4 text-left')}>Parameter</th>
              {months.map(([k, label]) => (
                <th key={k} className={clsx(th, 'min-w-[6.5rem] text-right', k === 'cur' ? 'bg-indigo-50/95 text-indigo-700' : 'text-slate-600')}>{label}</th>
              ))}
              {days.map((iso) => <DayTh key={iso} iso={iso} active={iso === data.date} />)}
            </tr>
          </thead>
          {sections.map((s) => (
            <tbody key={s.id}>
              <tr>
                <td colSpan={cols} className="border-t border-slate-200 bg-slate-50/60 p-0">
                  <div className="sticky left-0 flex w-max items-center gap-2 px-4 py-2">
                    <span className={clsx('h-3 w-1 rounded-full', s.accent)} />
                    <span className="text-[11.5px] font-semibold uppercase tracking-wide text-slate-600">{s.title}</span>
                  </div>
                </td>
              </tr>
              {s.rows.map((r) => (
                <tr key={r.key} className="group">
                  <th
                    scope="row"
                    className={clsx(labelCell, 'border-b border-slate-100 group-hover:bg-slate-50',
                      r.strong ? 'font-semibold text-slate-900' : r.target ? 'font-normal text-slate-400' : 'font-normal text-slate-700')}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      {r.label}
                      {r.source ? <span title={r.source} className="text-slate-300 hover:text-slate-500"><Info size={12} /></span> : null}
                    </span>
                  </th>
                  {months.map(([k, , m]) => {
                    const v = r.month ? pick(m, r.key) : undefined
                    return (
                      <td key={k} className={clsx(td, 'border-b border-slate-100 group-hover:bg-slate-50', k === 'cur' && 'bg-indigo-50/30',
                        r.strong && 'font-semibold', r.target ? 'text-slate-400' : 'text-slate-800', r.tone && pctTone(v))}>
                        {r.month ? fmtCell(v, r.kind) : ''}
                      </td>
                    )
                  })}
                  {days.map((iso) => {
                    const d = byDate.get(iso)
                    const v = d && d.has_data !== false ? pick(d, r.key) : undefined
                    const active = iso === data.date
                    return (
                      <td key={iso} className={clsx(td, 'border-b border-slate-100 group-hover:bg-slate-50',
                        active && 'bg-amber-50/50', r.strong && 'font-semibold',
                        r.target ? 'text-slate-400' : 'text-slate-800', r.tone && pctTone(v))}>
                        {fmtCell(v, r.kind) || <span className="text-slate-300">—</span>}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          ))}
        </table>
      </div>
      <p className="px-1 text-[11.5px] text-slate-400">
        Not tracked in telemetry, so blank in the Excel too: {UNTRACKED_ROWS.join(' · ')}.
      </p>
    </div>
  )
}

/* ------------------------------------------------------------------ Kms */
function heat(v: number | undefined, target: number, low: number): string {
  if (v == null) return 'text-slate-300'
  if (v <= low) return 'bg-rose-100 text-rose-800'
  if (v >= target) return 'bg-emerald-100 text-emerald-900'
  if (v < target / 2) return 'bg-amber-100 text-amber-900'
  return 'bg-slate-100 text-slate-800'
}

type Sort = 'mtd-desc' | 'mtd-asc' | 'vehicle' | 'today-asc'

export function KmsSheet({ data, days }: { data: DorReportPayload; days: string[] }) {
  const { kms, targets } = data
  const [q, setQ] = useState('')
  const [sort, setSort] = useState<Sort>('mtd-desc')
  const ref = useScrollToEnd(`${data.date}:${days.length}`)
  const pct = (v: number | null | undefined) => (v == null ? '—' : `${Math.round(v * 100)}%`)

  const vehicles = useMemo(() => {
    const needle = q.trim().toUpperCase()
    const rows = kms.vehicles.filter((v) => !needle || v.vehicle_id.includes(needle))
    const today = (v: DorVehicle) => v.days[data.date] ?? -1
    const cmp: Record<Sort, (a: DorVehicle, b: DorVehicle) => number> = {
      'mtd-desc': (a, b) => (b.mtd ?? -1) - (a.mtd ?? -1),
      'mtd-asc': (a, b) => (a.mtd ?? -1) - (b.mtd ?? -1),
      'today-asc': (a, b) => today(a) - today(b),
      vehicle: (a, b) => a.vehicle_id.localeCompare(b.vehicle_id),
    }
    return [...rows].sort(cmp[sort])
  }, [kms.vehicles, q, sort, data.date])

  const todayVals = kms.vehicles.map((v) => v.days[data.date]).filter((x): x is number => x != null)
  const hit = todayVals.filter((x) => x >= targets.km_per_veh_day).length
  const low = todayVals.filter((x) => x <= targets.active_min_km).length
  const mtdTarget = targets.kms_day * (data.months.cur.days || 1)
  const footer: [string, 'average_r' | 'average' | 'low_count' | 'sum'][] = [
    ['Average (running)', 'average_r'], ['Average (all)', 'average'], [`≤ ${targets.active_min_km} km`, 'low_count'], ['Total km', 'sum'],
  ]
  const lead = 4 + kms.weeks.length

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <label className="relative w-full sm:w-auto">
          <Search size={13} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            className={clsx(reportInput, 'h-8 w-full pl-8 sm:w-52')}
            placeholder="Search vehicle"
            aria-label="Search vehicle"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </label>
        <label className="inline-flex items-center gap-1.5 text-[12px] text-slate-500">
          <ArrowDownUp size={13} />
          <select className={clsx(reportInput, 'h-8')} aria-label="Sort vehicles" value={sort} onChange={(e) => setSort(e.target.value as Sort)}>
            <option value="mtd-desc">MTD km — high to low</option>
            <option value="mtd-asc">MTD km — low to high</option>
            <option value="today-asc">{dayHead(data.date).d} km — low to high</option>
            <option value="vehicle">Vehicle number</option>
          </select>
        </label>
        <div className="ml-auto flex flex-wrap items-center gap-3 text-[11.5px] text-slate-500">
          <span><b className="text-slate-800">{hit}</b> hit {fmt0(targets.km_per_veh_day)} km</span>
          <span><b className={low ? 'text-rose-700' : 'text-slate-800'}>{low}</b> ≤ {targets.active_min_km} km</span>
          <span className="hidden h-4 w-px bg-slate-200 sm:block" />
          {[
            ['bg-emerald-100', `≥ ${fmt0(targets.km_per_veh_day)}`],
            ['bg-slate-100', 'on the road'],
            ['bg-amber-100', `< ${fmt0(targets.km_per_veh_day / 2)}`],
            ['bg-rose-100', `≤ ${targets.active_min_km}`],
          ].map(([c, l]) => (
            <span key={l} className="inline-flex items-center gap-1"><span className={clsx('h-2.5 w-2.5 rounded-sm', c)} />{l}</span>
          ))}
        </div>
      </div>

      <div ref={ref} className={wrap}>
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr>
              <th className={clsx(th, 'left-0 z-[3] min-w-[9.5rem] px-4 text-left')}>Vehicle</th>
              <th className={clsx(th, 'min-w-[9rem] bg-indigo-50/95 text-right text-indigo-700')}>MTD {data.months.cur.label}</th>
              <th className={clsx(th, 'text-right')} title="MTD km against the day target × days elapsed">Pro-rata</th>
              <th className={clsx(th, 'text-right')} title={`Days at or above ${fmt0(targets.kms_day)} km`}>Tgt days</th>
              {kms.weeks.map(([a, b], i) => (
                <th key={a} className={clsx(th, 'text-right')} title={`Wed–Tue week ${a} → ${b}`}>W{i + 1}</th>
              ))}
              <th className={clsx(th, 'text-right text-slate-600')}>{data.months.prev.label}</th>
              {days.map((iso) => <DayTh key={iso} iso={iso} active={iso === data.date} />)}
              <th className={clsx(th, 'text-right')} title="Average of days with km > 0, this month">Avg (R)</th>
            </tr>
          </thead>
          <tbody>
            {vehicles.map((v) => {
              const share = v.mtd != null ? Math.min(1, v.mtd / mtdTarget) : 0
              return (
                <tr key={v.vehicle_id} className="group">
                  <th scope="row" className={clsx(labelCell, 'border-b border-slate-100 font-mono text-[12px] font-semibold text-slate-800 group-hover:bg-slate-50')}>
                    {v.vehicle_id}
                  </th>
                  <td className={clsx(td, 'border-b border-slate-100 bg-indigo-50/30 group-hover:bg-slate-50')}>
                    <div className="flex items-center justify-end gap-2">
                      <div className="h-1.5 w-12 overflow-hidden rounded-full bg-slate-100">
                        <div className="h-full rounded-full bg-indigo-500" style={{ width: `${share * 100}%` }} />
                      </div>
                      <span className="font-semibold text-slate-900">{v.mtd != null ? fmt0(v.mtd) : '—'}</span>
                    </div>
                  </td>
                  <td className={clsx(td, 'border-b border-slate-100 group-hover:bg-slate-50', pctTone(v.prorata_util))}>{pct(v.prorata_util)}</td>
                  <td className={clsx(td, 'border-b border-slate-100 text-slate-600 group-hover:bg-slate-50')}>{pct(v.target_days_pct)}</td>
                  {v.weeks.map((w, i) => <td key={i} className={clsx(td, 'border-b border-slate-100 group-hover:bg-slate-50', pctTone(w))}>{pct(w)}</td>)}
                  <td className={clsx(td, 'border-b border-slate-100 text-slate-500 group-hover:bg-slate-50')}>{v.prev_month != null ? fmt0(v.prev_month) : '—'}</td>
                  {days.map((iso) => {
                    const x = v.days[iso]
                    return (
                      <td key={iso} className={clsx('border-b border-slate-100 px-1 py-1 group-hover:bg-slate-50', iso === data.date && 'bg-amber-50/50')}>
                        <div className={clsx('rounded-md px-2 py-[3px] text-right text-[12px] tabular-nums', heat(x, targets.km_per_veh_day, targets.active_min_km), iso === data.date && 'font-semibold')}>
                          {x != null ? fmt0(x) : '—'}
                        </div>
                      </td>
                    )
                  })}
                  <td className={clsx(td, 'border-b border-slate-100 text-slate-600 group-hover:bg-slate-50')}>{v.cur_stats.average_r != null ? fmt0(v.cur_stats.average_r) : '—'}</td>
                </tr>
              )
            })}
            {vehicles.length === 0 ? (
              <tr><td colSpan={lead + 2 + days.length} className="px-4 py-8 text-center text-[12.5px] text-slate-400">No vehicle matches “{q}”.</td></tr>
            ) : null}
          </tbody>
          <tfoot>
            {footer.map(([label, k], i) => (
              <tr key={k} className="bg-slate-50">
                <th scope="row" className={clsx(labelCell, 'bg-slate-50 font-semibold text-slate-700', i === 0 && 'border-t-2 border-slate-200')}>{label}</th>
                <td colSpan={lead} className={clsx('bg-slate-50', i === 0 && 'border-t-2 border-slate-200')} />
                {days.map((iso) => {
                  const x = kms.footer[iso]?.[k]
                  return (
                    <td key={iso} className={clsx(td, 'font-semibold text-slate-700', i === 0 && 'border-t-2 border-slate-200', iso === data.date && 'bg-amber-50/60')}>
                      {x == null ? '—' : k === 'low_count' ? x : k === 'sum' ? fmt0(x) : fmt1(x)}
                    </td>
                  )
                })}
                <td className={clsx('bg-slate-50', i === 0 && 'border-t-2 border-slate-200')} />
              </tr>
            ))}
          </tfoot>
        </table>
      </div>
    </div>
  )
}
